<script lang="ts">
    import { onDestroy } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { currentUser } from '$lib/stores/auth';
    import { getHealth, type HealthLastSync } from '$lib/api';

    let sync: HealthLastSync | null = null;

    // Fetch health at component init (not inside onMount) so it works in jsdom tests.
    // Only fetch if current user is admin. We subscribe to currentUser to react to
    // login state changes, but we only care about the first non-null value.
    async function fetchHealth() {
        if (!$currentUser || $currentUser.role !== 'admin') return;
        try {
            const h = await getHealth();
            sync = h.last_sync;
        } catch {
            // swallow — chip is optional; don't disrupt the UI on health check failure
        }
    }

    const unsub = currentUser.subscribe(() => {
        fetchHealth();
    });
    onDestroy(unsub);

    function label(s: HealthLastSync): string {
        if (s.age_minutes == null) return 'Never synced';
        if (s.age_minutes < 60) return `Synced ${s.age_minutes}m ago`;
        const h = Math.round(s.age_minutes / 60);
        return `Synced ${h}h ago`;
    }

    function chipClass(s: HealthLastSync): string {
        if (s.age_minutes == null || s.age_minutes > 24 * 60) return 'red';
        if (s.age_minutes > 12 * 60) return 'amber';
        return '';
    }
</script>

{#if $currentUser?.role === 'admin' && sync}
    <button
        class="freshness-chip {chipClass(sync)}"
        on:click={() => push('/admin/status')}
        title="Go to admin status"
    >
        {label(sync)}
    </button>
{/if}

<style>
    .freshness-chip {
        font-family: var(--font-mono);
        padding: 4px 10px;
        font-size: 0.72rem;
        font-weight: 500;
        letter-spacing: 0.02em;
        border: 1px solid rgba(247, 243, 234, 0.3);
        background: rgba(247, 243, 234, 0.08);
        color: var(--leaf-soft);
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s;
    }
    .freshness-chip:hover {
        background: rgba(247, 243, 234, 0.16);
        border-color: rgba(247, 243, 234, 0.5);
    }
    .freshness-chip.amber {
        background: rgba(229, 178, 64, 0.18);
        border-color: rgba(229, 178, 64, 0.5);
        color: var(--sun-bright);
    }
    .freshness-chip.red {
        background: rgba(155, 51, 64, 0.25);
        border-color: rgba(155, 51, 64, 0.6);
        color: #FFB4B4;
    }

    @media (max-width: 520px) {
        .freshness-chip { padding: 3px 8px; font-size: 0.68rem; }
    }
</style>
